import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from 'react';
import { Button, Tooltip, Typography, App, Spin } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, ClockCircleOutlined, DeleteOutlined, ExportOutlined } from '@ant-design/icons';
import {
  instrumentConfigApi,
  InstrumentConfigResponse,
} from '../../../services/instrumentConfigApi';
import DepositWithdrawModal from './DepositWithdrawModal';
import GridEndFromBrokerModal from './GridEndFromBrokerModal';
import { formatNumber } from '../../../utils/numberFormat';

const { Text } = Typography;

export interface InstrumentCapitalPanelHandle {
  /** Re-fetch portfolio configs (call after the user saves a grid config). */
  refresh: () => Promise<void>;
}

interface Props {
  portfolioId?: string | null;
  /** Symbol the user is currently editing. Used as the target of deposit/withdraw actions. */
  editingSymbol?: string | null;
  /** True when editingSymbol is saved in the grid config (i.e. its InstrumentConfig exists). */
  isSaved: boolean;
  /**
   * Fires after each successful list-fetch with the latest Σ currentCapital from
   * `aggregates.totalDeposited`. The parent uses it for allocation-exceeds checks
   * (the panel is already polling the list, so the parent doesn't refetch).
   */
  onTotalCapitalChange?: (total: number) => void;
  poll?: boolean;
  /** Bump this to force a re-fetch (e.g. tab re-entry). */
  refreshKey?: number;
  /** The grid bot is up: End from broker needs it stopped. */
  botRunning?: boolean;
  dataConfigId?: string | null;
  /**
   * Fires once the End from broker job is READY. null: the job followed was another import
   * open on the account (a 409 attaches to it), so no symbol left the grid.
   */
  onSymbolEnded?: (symbol: string | null) => void;
}

/**
 * End from broker: offered only on a symbol holding shares (with none, Delete does it),
 * enabled only while the bot is stopped.
 */
export function endFromBrokerState(opts: {
  canAct: boolean;
  heldQty: number;
  botRunning: boolean;
  botStopping: boolean;
}): { visible: boolean; enabled: boolean; hint: string } {
  const visible = opts.canAct && opts.heldQty > 0;
  const hint = opts.botStopping
    ? 'The bot is stopping — End from broker opens once it has stopped.'
    : opts.botRunning
      ? 'Stop the grid bot to end this symbol from broker.'
      : 'End from broker: take this symbol out of the grid without selling. Its shares go back to the unclaimed holdings.';
  return { visible, enabled: visible && !opts.botRunning && !opts.botStopping, hint };
}

const POLL_MS = 15_000;
const STOPPING_POLL_MS = 5_000;

const formatVND = (n: number): string =>
  formatNumber(n, { fallback: '0' });

const sumDecimal = (rows: InstrumentConfigResponse[], key: keyof Pick<InstrumentConfigResponse, 'initialCapital' | 'currentCapital' | 'holdingsCost'>): number =>
  rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

const InstrumentCapitalPanel = forwardRef<InstrumentCapitalPanelHandle, Props>((
  { portfolioId, editingSymbol, isSaved, onTotalCapitalChange, poll = true, refreshKey, botRunning = false, dataConfigId, onSymbolEnded },
  ref,
) => {
  const { message, modal } = App.useApp();
  const [items, setItems] = useState<InstrumentConfigResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalMode, setModalMode] = useState<'DEPOSIT' | 'WITHDRAW' | null>(null);
  // snapshot at open: the row leaves the list once the job is READY
  const [endTarget, setEndTarget] = useState<{ symbol: string; heldQty: number } | null>(null);
  const [botStopping, setBotStopping] = useState(false);
  const [pendingDeposit, setPendingDeposit] = useState(0);
  const [pendingWithdraw, setPendingWithdraw] = useState(0);

  const bareEditing = (editingSymbol ?? '').includes(':')
    ? (editingSymbol ?? '').split(':').slice(1).join(':')
    : (editingSymbol ?? '');

  /**
   * silent=true skips the loading spinner and skips committing identical results.
   * This prevents background polls from flickering the loading state or triggering
   * downstream re-renders (which the user perceives as button lag).
   */
  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!portfolioId) {
      setItems([]);
      onTotalCapitalChange?.(0);
      return;
    }
    try {
      if (!opts.silent) setLoading(true);
      const res = await instrumentConfigApi.list({
        portfolio_id: portfolioId,
        config_type: 'GRID',
        page_size: 100,
      });
      // Bubble Σ currentCapital up so the parent's `myCash` stays in sync with
      // every poll/refresh. Server is the source of truth — aggregates are
      // computed across the full filter, not just the current page.
      onTotalCapitalChange?.(Number(res.aggregates?.totalDeposited) || 0);
      setBotStopping(res.strategyStopping === true);
      setItems((prev) => {
        // Cheap shallow signature: same length + same per-id capital/MTM → skip the
        // setState entirely. Avoids re-rendering the host ConfigurationSection every poll.
        if (prev.length === res.items.length) {
          const same = prev.every((p, i) => {
            const n = res.items[i];
            return (
              n &&
              p.id === n.id &&
              p.currentCapital === n.currentCapital &&
              p.holdingsCost === n.holdingsCost &&
              p.heldQty === n.heldQty &&
              p.marketValue === n.marketValue &&
              p.status === n.status
            );
          });
          if (same) return prev;
        }
        return res.items;
      });
    } catch (e: unknown) {
      if (!opts.silent) {
        const msg = e instanceof Error ? e.message : String(e);
        message.error(`Failed to load capital: ${msg}`);
      }
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [portfolioId, message]);

  useImperativeHandle(ref, () => ({ refresh: () => load() }), [load]);

  // Reload when portfolio changes (load callback dep) or when parent bumps refreshKey
  // (e.g. user clicks back into the Grid Config tab). User-triggered → not silent.
  useEffect(() => { void load(); }, [load, refreshKey]);

  // Background polling — silent, no spinner, no setState when nothing changed.
  useEffect(() => {
    if (!poll || !portfolioId) return;
    // Faster while stopping, so the buttons come back soon after the bot has stopped.
    const id = window.setInterval(
      () => { void load({ silent: true }); },
      botStopping ? STOPPING_POLL_MS : POLL_MS,
    );
    return () => window.clearInterval(id);
  }, [poll, portfolioId, load, botStopping]);

  const targetConfig = useMemo(
    () => items.find((c) => c.symbol === bareEditing) ?? null,
    [items, bareEditing],
  );

  // Pending sums (DEPOSIT/WITHDRAW awaiting bot ack) for the current target.
  // Reset to 0 when target changes; refreshed by the effect below.
  useEffect(() => {
    setPendingDeposit(0);
    setPendingWithdraw(0);
    if (!targetConfig?.id) return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const res = await instrumentConfigApi.listTransactions(targetConfig.id, { page_size: 1 });
        if (cancelled) return;
        setPendingDeposit(Number(res.pendingDeposit) || 0);
        setPendingWithdraw(Number(res.pendingWithdraw) || 0);
      } catch {
        // Soft-fail: indicator stays at 0; not critical for the panel to function.
      }
    };
    void fetchPending();
    const id = window.setInterval(fetchPending, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [targetConfig?.id, refreshKey]);

  const canAct = isSaved && targetConfig !== null && targetConfig.status !== 'CLOSED';
  // A stopping worker has left its cash-op queue; the server answers 409 until it has stopped.
  const canCashOp = canAct && !botStopping;
  const cashOpHint = canAct && botStopping
    ? 'The bot is stopping — deposit / withdraw reopen once it has stopped.'
    : '';
  const actionHint = !editingSymbol
    ? 'Select a symbol from the bar above to deposit / withdraw.'
    : !isSaved
      ? `Save the grid config for ${editingSymbol} first to enable deposit / withdraw.`
      : !targetConfig
        ? `No instrument config found for ${editingSymbol}.`
        : '';

  const handleSuccess = (updated: InstrumentConfigResponse) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
    setModalMode(null);
    message.success('Submitted');
    // Silent follow-up: if the op was PENDING, the bot ack will flip currentCapital;
    // we re-fetch without flickering the spinner to avoid button-lag perception.
    window.setTimeout(() => { void load({ silent: true }); }, 1500);
  };

  // Delete is allowed only once the allocation is fully drained: no committed cash,
  // no open position, and no in-flight cash op. Mirrors the BE guard (409 otherwise).
  const isDrained =
    targetConfig != null &&
    Number(targetConfig.currentCapital) === 0 &&
    Number(targetConfig.holdingsCost) === 0 &&
    Number(targetConfig.heldQty) === 0;
  const canDelete = canAct && isDrained && pendingDeposit === 0 && pendingWithdraw === 0;
  const deleteHint = !canAct
    ? (actionHint || 'Save the config first.')
    : !isDrained
      ? 'Withdraw all capital and close positions before deleting this allocation.'
      : pendingDeposit > 0 || pendingWithdraw > 0
        ? 'Settle the pending deposit/withdraw before deleting.'
        : 'Delete this fully-drained allocation (history is kept).';

  const endState = endFromBrokerState({
    canAct,
    heldQty: Number(targetConfig?.heldQty) || 0,
    botRunning,
    botStopping,
  });

  const handleDelete = () => {
    if (!targetConfig?.id) return;
    const { id, symbol } = targetConfig;
    modal.confirm({
      title: `Delete allocation ${symbol}?`,
      content:
        'This closes the fully-drained allocation and removes the symbol from the grid config. ' +
        'Deposit/withdraw history is kept. You can re-add the symbol later.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await instrumentConfigApi.remove(id);
          message.success(`Deleted allocation ${symbol}`);
          setItems((prev) => prev.filter((it) => it.id !== id));
          void load({ silent: true });
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : String(e);
          message.error(`Delete failed: ${msg}`);
        }
      },
    });
  };

  return (
    <>
      <div data-tour="grid-capital" style={panelStyle}>
        {/* Delete sits in the corner, icon-only: it is a rare, destructive action
            and does not deserve a third of the action row next to the two buttons
            used every session. Wrapped in a span so the tooltip still shows while
            the button is disabled. */}
        {endState.visible && (
          <Tooltip title={endState.hint} placement="left">
            <span style={{ position: 'absolute', top: 4, right: 30 }}>
              <Button
                size="small"
                type="text"
                danger
                aria-label="End from broker"
                icon={<ExportOutlined />}
                onClick={() => targetConfig && setEndTarget({ symbol: targetConfig.symbol, heldQty: Number(targetConfig.heldQty) || 0 })}
                disabled={!endState.enabled}
                style={{ height: 22, width: 22, minWidth: 22, padding: 0 }}
              />
            </span>
          </Tooltip>
        )}
        <Tooltip title={deleteHint} placement="left">
          <span style={{ position: 'absolute', top: 4, right: 6 }}>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              disabled={!canDelete}
              style={{ height: 22, width: 22, minWidth: 22, padding: 0 }}
            />
          </span>
        </Tooltip>

        <div style={endState.visible ? { ...statsRowStyle, paddingRight: 48 } : statsRowStyle}>
          <Stat
            label={editingSymbol ? `Capital in ${editingSymbol}` : 'Capital in symbol'}
            tooltip="The capital committed to this symbol — what you deposited, plus the realized PnL already credited back to it. This is the budget the bot uses."
            value={
              targetConfig
                ? formatVND(Number(targetConfig.currentCapital))
                : editingSymbol
                  ? (isSaved ? '0' : '—')
                  : '—'
            }
            highlight
          />
          <Divider />
          <Stat
            label={editingSymbol ? `Market value` : 'Market value'}
            tooltip={
              targetConfig?.marketPrice
                ? `Cash + held qty × latest matched price. ` +
                  `Refreshed each time you switch back to this tab. ` +
                  `Last price used: ${formatVND(Number(targetConfig.marketPrice))} VND/share, ` +
                  `qty: ${formatVND(Number(targetConfig.heldQty))}.`
                : `Allocated cash marked to market: cash + (held qty × market price − position cost). ` +
                  `When no live price is available, falls back to the allocated cash. ` +
                  `Refreshed each time you switch back to this tab.`
            }
            value={
              targetConfig
                ? formatVND(
                    Number(
                      // Fallback = mark at cost = the allocated cash itself (currentCapital
                      // already contains holdingsCost; re-adding it double-counts the position).
                      targetConfig.marketValue ?? targetConfig.currentCapital,
                    ),
                  )
                : editingSymbol
                  ? (isSaved ? '0' : '—')
                  : '—'
            }
          />
          {(pendingDeposit > 0 || pendingWithdraw > 0) && (
            <PendingTag deposit={pendingDeposit} withdraw={pendingWithdraw} />
          )}
        </div>

        {/* Deposit / Withdraw share the full width, one half each — same shape as
            the pair page's cash-op row. `flex: 1` beats a wrapping right-aligned
            row: the buttons stay side by side and legible however narrow the
            config column is dragged, instead of wrapping one under the other. */}
        <div style={{ display: 'flex', gap: 6, width: '100%' }}>
          <Tooltip title={actionHint || cashOpHint || 'Add cash to the symbol you are editing'} placement="top">
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              onClick={() => setModalMode('DEPOSIT')}
              disabled={!canCashOp}
              style={{
                flex: 1,
                height: '32px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '4px',
                background: canCashOp ? '#0ECB81' : undefined,
                borderColor: canCashOp ? '#0ECB81' : undefined,
                color: canCashOp ? '#fff' : undefined,
              }}
            >
              Deposit
            </Button>
          </Tooltip>
          <Tooltip title={actionHint || cashOpHint || 'Remove cash from the symbol you are editing'} placement="top">
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              onClick={() => setModalMode('WITHDRAW')}
              disabled={!canCashOp}
              style={{
                flex: 1,
                height: '32px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '4px',
                background: canCashOp ? '#F6465D' : undefined,
                borderColor: canCashOp ? '#F6465D' : undefined,
                color: canCashOp ? '#fff' : undefined,
              }}
            >
              Withdraw
            </Button>
          </Tooltip>
        </div>

        {loading && items.length > 0 ? (
          <Spin size="small" style={{ marginLeft: 4 }} />
        ) : null}
      </div>

      {portfolioId && !loading && items.length === 0 ? (
        <div
          style={{
            color: '#848E9C',
            textAlign: 'center',
            padding: '10px 12px',
            marginTop: 8,
            border: '1px dashed #2B3139',
            borderRadius: 6,
            fontSize: 12,
          }}
        >
          No allocations yet. Save the config and deposit to fund a symbol.
        </div>
      ) : null}

      <DepositWithdrawModal
        open={modalMode !== null && targetConfig !== null}
        mode={modalMode ?? 'DEPOSIT'}
        config={targetConfig}
        portfolioId={portfolioId}
        onCancel={() => setModalMode(null)}
        onSuccess={handleSuccess}
      />

      <GridEndFromBrokerModal
        open={endTarget !== null}
        onClose={() => setEndTarget(null)}
        dataConfigId={dataConfigId ?? null}
        symbol={endTarget?.symbol ?? null}
        heldQty={endTarget?.heldQty ?? 0}
        onEnded={(job) => {
          void load({ silent: true });
          onSymbolEnded?.(job.kind === 'GRID_SYMBOL_END' && endTarget ? endTarget.symbol : null);
        }}
      />
    </>
  );
});

InstrumentCapitalPanel.displayName = 'InstrumentCapitalPanel';

// Column now: the stats keep their own horizontal row, and the action row sits
// under them at full width. `relative` anchors the corner Delete button.
const panelStyle: React.CSSProperties = {
  position: 'relative',
  background: '#1E2329',
  border: '1px solid #2B3139',
  borderRadius: 6,
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 8,
};

// Right padding leaves room for the absolutely-positioned Delete button so a long
// symbol name can't slide underneath it.
const statsRowStyle: React.CSSProperties = {
  // Equal tracks rather than flex-wrap: sized to their own content, a long VND figure
  // widened one stat and bumped the other onto a new line, so the row's shape changed with
  // the size of the account.
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 45%), 1fr))',
  alignItems: 'center',
  gap: 10,
  paddingRight: 24,
};

const Stat: React.FC<{
  label: string;
  value: string;
  tooltip?: string;
  highlight?: boolean;
  mono?: boolean;
}> = ({ label, value, tooltip, highlight, mono }) => {
  const labelEl = (
    <Text
      type="secondary"
      style={{
        fontSize: 10,
        lineHeight: 1.1,
        letterSpacing: 0.3,
        cursor: tooltip ? 'help' : undefined,
        borderBottom: tooltip ? '1px dotted #848E9C' : undefined,
        alignSelf: 'flex-start',
      }}
    >
      {label}
    </Text>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {tooltip ? <Tooltip title={tooltip}>{labelEl}</Tooltip> : labelEl}
      {/* Clips instead of widening its column; the exact figure stays in the title. */}
      <Text
        strong
        title={typeof value === 'string' ? value : undefined}
        style={{
          color: highlight ? '#0ECB81' : '#E0E0E0',
          fontSize: 13,
          lineHeight: 1.2,
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Text>
    </div>
  );
};

const PendingTag: React.FC<{ deposit: number; withdraw: number }> = ({ deposit, withdraw }) => {
  const parts: string[] = [];
  if (deposit > 0) parts.push(`+${formatVND(deposit)}`);
  if (withdraw > 0) parts.push(`−${formatVND(withdraw)}`);
  const label = parts.join('  ·  ');
  const tooltip = [
    deposit > 0 ? `Deposit pending: ${formatVND(deposit)} VND` : null,
    withdraw > 0 ? `Withdraw pending: ${formatVND(withdraw)} VND` : null,
    'Bot has not acknowledged yet — see Capital tab for details.',
  ].filter(Boolean).join('\n');
  return (
    <Tooltip title={tooltip}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 4,
          background: 'rgba(240, 185, 11, 0.12)',
          border: '1px solid rgba(240, 185, 11, 0.45)',
          color: '#F0B90B',
          fontSize: 11,
          fontVariantNumeric: 'tabular-nums',
          cursor: 'help',
        }}
      >
        <ClockCircleOutlined style={{ fontSize: 10 }} spin />
        <span>Pending</span>
        <span style={{ color: '#E0E0E0' }}>{label}</span>
      </div>
    </Tooltip>
  );
};

const Divider: React.FC = () => (
  <div style={{ width: 1, alignSelf: 'stretch', background: '#2B3139', margin: '2px 0' }} />
);

export default InstrumentCapitalPanel;
